import React from 'react';
import { useSearchParams } from 'react-router-dom';

import Helmet from 'react-helmet';
import { useRequiredHermes } from 'ha/modules/Hermes';
import { HermesMeta } from 'ha/components.legacy/HermesMeta';
import { BlogTypes } from 'ha/constants/BlogTypes';
import { fixHref } from 'ha/utils/urls/fixHref';
import { BreadcrumbsWrapper } from '@ha/dsl';

import { ContentfulImage } from 'ha/modules/Hermes/types';
import { BlogLandingMain } from './BlogLandingMain';
import { Blog } from './Blog';
import { CTA_QUERY_PARAM } from '../constants';
import { HermesData } from '../types';
import { BlogBreadcrumbs } from './BlogBreadcrumbs';

export function getStructuredData({
  author,
  title,
  headerImage,
  headerImageAsset,
}: {
  author: { name: string; date?: string };
  title: string;
  headerImage?: string;
  headerImageAsset?: ContentfulImage;
}) {
  const image = headerImageAsset?.fields.url || headerImage;
  return {
    '@context': 'http://schema.org',
    '@type': 'Article',
    author: author.name,
    publisher: {
      '@type': 'Organization',
      name: 'HousingAnywhere',
      logo: {
        '@type': 'ImageObject',
        name: 'HousingAnywhere Logo',
        width: '60',
        height: '60',
        url: 'https://housinganywhere.com/dist/icon_60.png',
      },
    },
    headline: title,
    image: image && [fixHref(image)],
    datePublished: author.date,
  };
}

export const BlogPageLayout: React.FC = () => {
  const { data, linkData } = useRequiredHermes<HermesData>();
  const [searchParams] = useSearchParams();
  const isCTADisabled = searchParams.has(CTA_QUERY_PARAM);
  const metaDescription = data.meta.description || '';
  const structuredData =
    data.blogType === BlogTypes.ADVERTISER ? getStructuredData(data) : null;

  return (
    <div className="blog">
      <BreadcrumbsWrapper>
        <BlogBreadcrumbs />
      </BreadcrumbsWrapper>
      <Helmet>
        {structuredData && (
          <script type="application/ld+json" id="structuredDataArticle">
            {JSON.stringify(structuredData)}
          </script>
        )}
        <meta key="meta og:type" property="og:type" content="article" />
        {data.headerImage && (
          <meta
            key="meta og:image"
            property="og:image"
            content={fixHref(
              data.headerImageAsset?.fields.url || data.headerImage,
            )}
          />
        )}
      </Helmet>
      <HermesMeta
        metaTitle={data.title}
        metaDescription={metaDescription}
        linkData={linkData}
        socialNetworkTitle={data.socialNetworkTitle}
        titleTemplateOn={false}
      />
      {data.blogType === BlogTypes.LANDING ||
      data.blogType === BlogTypes.PRESS ? (
        <BlogLandingMain data={data} />
      ) : (
        <Blog data={data} isCTADisabled={isCTADisabled} />
      )}
    </div>
  );
};
